import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus, MoreVertical, Info, CheckCircle2 } from "lucide-react";

function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallGuide({ open, onClose }) {
  const [platform, setPlatform] = useState("android");

  useEffect(() => {
    if (open) setPlatform(detectPlatform());
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/80 backdrop-blur-sm sm:items-center"
          onClick={onClose}
          data-testid="install-guide"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-3xl border border-zinc-800 bg-zinc-950 p-6 sm:rounded-3xl"
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Install DailyRacer
                </div>
                <div className="font-display text-2xl text-white">Add to home screen</div>
              </div>
              <button
                onClick={onClose}
                data-testid="install-guide-close"
                className="rounded-full border border-zinc-800 p-2 text-zinc-500 hover:text-white"
              >
                <X size={14} />
              </button>
            </div>

            {/* Platform tabs */}
            <div className="mb-5 flex rounded-full border border-zinc-800 bg-zinc-900 p-1">
              {[
                { k: "ios", label: "iPhone" },
                { k: "android", label: "Android" },
              ].map((tab) => (
                <button
                  key={tab.k}
                  onClick={() => setPlatform(tab.k)}
                  data-testid={`install-tab-${tab.k}`}
                  className={`flex-1 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest transition ${
                    platform === tab.k
                      ? "bg-[#E1FF00] text-black"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {isStandalone() ? (
              <div className="flex items-center gap-3 rounded-2xl border border-[#4ADE80]/30 bg-[#4ADE80]/5 p-4">
                <CheckCircle2 size={20} className="text-[#4ADE80]" />
                <div className="text-sm text-zinc-300">
                  You've already installed DailyRacer. You're all set.
                </div>
              </div>
            ) : platform === "ios" ? (
              <ol className="space-y-3">
                <Step
                  n={1}
                  title="Open this page in Safari"
                  body="If you're in another browser (like Chrome), tap the ⋯ menu → Open in Safari. Add to Home Screen only works in Safari."
                />
                <Step
                  n={2}
                  title={
                    <>
                      Tap the <Share size={14} className="inline align-[-2px]" /> Share button
                    </>
                  }
                  body="It's at the bottom of the screen — a square with an arrow pointing up."
                />
                <Step
                  n={3}
                  title={
                    <>
                      Scroll down and tap <span className="rounded bg-white/10 px-2 py-0.5 font-semibold text-zinc-200"><Plus size={11} className="inline align-[-1px]" /> Add to Home Screen</span>
                    </>
                  }
                  body="It's in the second row of options in the Share sheet."
                />
                <Step n={4} title="Tap Add (top right)" body="DailyRacer icon appears on your home screen." />
                <Step
                  n={5}
                  warn
                  title="Turn ON alerts / bubbles"
                  body='Open DailyRacer from your home screen (NOT Safari) → tap the yellow ALERTS button → tap "Allow" when iOS asks. Without this you will NOT hear the horn or see the bubble when a tip lands.'
                />
              </ol>
            ) : (
              <ol className="space-y-3">
                <Step
                  n={1}
                  title="Open this page in Chrome"
                  body="If you're in Samsung Browser or another app, tap the menu → Open in Chrome for the smoothest install."
                />
                <Step
                  n={2}
                  title={
                    <>
                      Tap the <MoreVertical size={14} className="inline align-[-2px]" /> three-dot menu (top right)
                    </>
                  }
                  body="Then tap Install app or Add to Home Screen."
                />
                <Step
                  n={3}
                  title="If you see 'Unsafe app' warning"
                  body='Tap "Install anyway" — it is safe. Google Play Protect flags brand-new domains as unknown. This is your own site.'
                  warn
                />
                <Step
                  n={4}
                  title="Open DailyRacer from your home screen"
                  body="Not from the browser tab — the app icon on your home screen."
                />
                <Step
                  n={5}
                  warn
                  title="Turn ON alerts / bubbles"
                  body='Inside the app, tap the yellow ALERTS button → tap "Allow" when Chrome asks. Without this you will NOT hear the horn or see the bubble when a tip lands.'
                />
              </ol>
            )}

            <div className="mt-5 flex items-start gap-2 rounded-xl border border-zinc-800 bg-black/40 p-3 text-[11px] text-zinc-500">
              <Info size={14} className="mt-0.5 flex-none text-zinc-400" />
              <div>
                Push alerts on iPhone only work if the app is added to your home screen. That's an Apple rule, not us.
              </div>
            </div>

            <button
              onClick={onClose}
              data-testid="install-guide-done"
              className="dr-btn-primary mt-5 w-full rounded-full px-6 py-3 text-sm"
            >
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Step({ n, title, body, warn }) {
  return (
    <li className={`flex gap-3 rounded-2xl border p-3 ${warn ? "border-[#E1FF00]/40 bg-[#E1FF00]/5" : "border-zinc-800 bg-zinc-900"}`}>
      <div className={`flex h-7 w-7 flex-none items-center justify-center rounded-full font-mono-data text-xs font-bold ${warn ? "bg-[#E1FF00] text-black" : "bg-zinc-800 text-[#E1FF00]"}`}>
        {n}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-sm leading-tight text-white">{title}</div>
        <p className="mt-1 text-xs leading-relaxed text-zinc-400">{body}</p>
      </div>
    </li>
  );
}

export { detectPlatform, isStandalone };
